import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CalendarClock, Info, Send, Users } from 'lucide-react';
import { Button } from '@/components/Button';
import { CampaignSteps } from '@/components/CampaignSteps';
import { Field, fieldInputClass } from '@/components/Field';
import { ApprovalPanel } from '@/components/ApprovalPanel';
import { ScheduleSendDialog } from '@/components/ScheduleSendDialog';
import { TestSendDialog } from '@/components/TestSendDialog';
import { Modal } from '@/components/Modal';
import { ErrorState, Skeleton } from '@/components/States';
import { useChecklist, useSendCampaign } from '@/hooks/use-send';
import { useRouteParam } from '@/hooks/use-route-param';
import { useCampaign } from '@/hooks/use-campaigns';
import { useBrand } from '@/hooks/use-brands';
import { useSetSender, useUnschedule } from '@/hooks/use-campaign-flow';
import { describeError } from '@/lib/describe-error';
import { formatDateTime } from '@/lib/utils';
import type { Session } from '@/auth/keycloak';
import type { CampaignDetail, ChecklistItem, ChecklistSeverity } from '@/lib/schemas';

/**
 * The send screen.
 *
 * ── THE ONE THING THIS SCREEN MUST GET RIGHT ─────────────────────────────────
 *
 * Sending cannot be undone. So the audience size and the blocking items are the largest things on the
 * page, the confirmation names the number of people, and the button is never the first thing your eye
 * lands on. A send screen that makes sending easy and reviewing hard is the wrong way round.
 *
 * The checklist is refetched on every visit with no stale window, because everything it reports can
 * change from under the author — a colleague approves it, somebody unsubscribes, the breaker trips.
 */
export function SendPage({ session }: { session: Session }) {
  const campaignId = useRouteParam('campaignId');
  const navigate = useNavigate();

  const campaign = useCampaign(campaignId);
  const checklist = useChecklist(campaignId);
  const send = useSendCampaign(campaignId);

  const [isConfirming, setIsConfirming] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);

  const unschedule = useUnschedule(campaignId);

  if (checklist.isPending) return <SendSkeleton />;

  if (checklist.isError) {
    return (
      <div className="px-4 py-6 sm:px-6">
        <ErrorState error={checklist.error} onRetry={() => void checklist.refetch()} />
      </div>
    );
  }

  const blocking = checklist.data.items.filter((item) => item.severity === 'Blocking');
  const warnings = checklist.data.items.filter((item) => item.severity === 'Warning');
  const information = checklist.data.items.filter((item) => item.severity === 'Information');

  return (
    <div className="px-4 py-6 sm:px-6">
      {/* The step nav, so this screen is part of a flow rather than a URL somebody had to know. */}
      {campaign.data !== undefined && (
        <div className="mb-4">
          <CampaignSteps campaign={campaign.data} />
        </div>
      )}

      <h1 className="text-xl font-semibold text-heading">
        Send {campaign.data?.name ?? 'this campaign'}
      </h1>

      {/* The audience size is the biggest thing on the page. It is recomputed server-side on every
          load rather than read from a cache, because it is the number somebody is approving. */}
      <div className="mt-5 flex flex-wrap items-center gap-4 rounded-lg border border-border bg-surface p-5">
        <Users className="h-8 w-8 shrink-0 text-muted" aria-hidden="true" />

        <div className="min-w-0">
          <p className="text-3xl font-semibold tabular-nums text-heading">
            {checklist.data.audienceSize.toLocaleString()}
          </p>
          <p className="text-sm text-muted">
            people will receive this, after suppression. Recomputed just now, not cached.
          </p>
        </div>
      </div>

      {/* The From address. It blocks the checklist while unset and had no control anywhere in the
          app, so a campaign could never satisfy it. Placed above the blocking list rather than buried
          below it, because when it IS the blocker this is the fix. */}
      {campaign.data !== undefined && <SenderPicker campaign={campaign.data} />}

      {/* The approval gate. Renders nothing when the brand does not require approval and none has
          ever been requested, so a single-sender brand never sees it. */}
      {campaign.data !== undefined && (
        <ApprovalPanel campaign={campaign.data} session={session} />
      )}

      {blocking.length > 0 && (
        <ChecklistGroup
          title={`${blocking.length} thing${blocking.length === 1 ? '' : 's'} stopping this send`}
          items={blocking}
          severity="Blocking"
        />
      )}

      {warnings.length > 0 && (
        <ChecklistGroup
          title={`${warnings.length} thing${warnings.length === 1 ? '' : 's'} worth checking`}
          items={warnings}
          severity="Warning"
        />
      )}

      {information.length > 0 && (
        <ChecklistGroup title="For reference" items={information} severity="Information" />
      )}

      {send.isError && (
        <p role="alert" className="mt-4 rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-ink">
          {describeError(send.error).message}
        </p>
      )}

      {send.isSuccess && (
        // Announced, because the outcome of the one irreversible action must reach a screen reader.
        <p
          role="status"
          className="mt-4 rounded-md bg-success-bg px-3 py-2 text-sm text-success-ink"
        >
          Queued. The worker is dispatching it now — the campaign page shows progress.
        </p>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => setIsTesting(true)}>
          Send a test to myself
        </Button>

        <Button
          variant="primary"
          disabled={!checklist.data.canSend || send.isPending || send.isSuccess}
          onClick={() => setIsConfirming(true)}
        >
          <Send className="h-4 w-4" aria-hidden="true" />
          {send.isPending ? 'Queueing…' : 'Send now'}
        </Button>

        {/* Scheduling is OUR scheduler holding the campaign, not the transport's send_at (§ 5.5).
            Offered beside Send now because they are the same decision with a different time. */}
        <Button
          variant="secondary"
          disabled={!checklist.data.canSend}
          onClick={() => setIsScheduling(true)}
        >
          <CalendarClock className="h-4 w-4" aria-hidden="true" />
          Schedule…
        </Button>

        <Button variant="ghost" onClick={() => void navigate(`/campaigns/${campaignId}`)}>
          Back to the editor
        </Button>
      </div>

      {/* Disabling the send button IS justified here, unlike a form's submit: the server will refuse
          it anyway, and the blocking list above already says exactly why in plain language. */}
      {!checklist.data.canSend && (
        <p className="mt-2 text-xs text-muted">
          Sending is unavailable until the {blocking.length} blocking item
          {blocking.length === 1 ? '' : 's'} above {blocking.length === 1 ? 'is' : 'are'} resolved.
        </p>
      )}

      {/* Already scheduled: the useful action is unscheduling, not scheduling again. */}
      {campaign.data?.scheduledAt !== null && campaign.data !== undefined && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-info-bg p-4">
          <CalendarClock className="h-5 w-5 shrink-0 text-info-ink" aria-hidden="true" />

          <p className="min-w-0 flex-1 text-sm text-info-ink">
            Scheduled for{' '}
            <strong className="font-semibold">{formatDateTime(campaign.data.scheduledAt)}</strong>.
            Editing the campaign means unscheduling first.
          </p>

          <Button
            variant="secondary"
            size="sm"
            disabled={unschedule.isPending}
            onClick={() => unschedule.mutate()}
          >
            {unschedule.isPending ? 'Unscheduling…' : 'Unschedule'}
          </Button>
        </div>
      )}

      {isScheduling && campaign.data !== undefined && (
        <ScheduleSendDialog
          campaignId={campaign.data.id}
          campaignName={campaign.data.name}
          audienceSize={checklist.data.audienceSize}
          onClose={() => setIsScheduling(false)}
        />
      )}

      {isConfirming && (
        <ConfirmSendDialog
          audienceSize={checklist.data.audienceSize}
          campaignName={campaign.data?.name ?? 'this campaign'}
          isPending={send.isPending}
          onCancel={() => setIsConfirming(false)}
          onConfirm={() => {
            send.mutate();
            setIsConfirming(false);
          }}
        />
      )}

      {isTesting && campaign.data !== undefined && (
        <TestSendDialog
          campaignId={campaignId}
          arms={campaign.data.arms}
          onClose={() => setIsTesting(false)}
        />
      )}
    </div>
  );
}

const severityStyles: Record<
  ChecklistSeverity,
  { border: string; icon: typeof Info; iconClass: string; label: string }
> = {
  Blocking: {
    border: 'border-danger',
    icon: AlertTriangle,
    iconClass: 'text-danger-ink',
    label: 'Blocking',
  },
  Warning: {
    border: 'border-warning',
    icon: AlertTriangle,
    iconClass: 'text-warning-ink',
    label: 'Warning',
  },
  Information: {
    border: 'border-border',
    icon: Info,
    iconClass: 'text-muted',
    label: 'Information',
  },
};

function ChecklistGroup({
  title,
  items,
  severity,
}: {
  title: string;
  items: ChecklistItem[];
  severity: ChecklistSeverity;
}) {
  const style = severityStyles[severity];
  const Icon = style.icon;

  return (
    <section className={`mt-5 overflow-hidden rounded-lg border-l-4 ${style.border} bg-surface`}>
      <h2 className="border-b border-border px-4 py-3 text-sm font-semibold text-heading">
        {title}
      </h2>

      <ul className="divide-y divide-border">
        {items.map((item) => (
          <li key={item.key} className="flex gap-3 px-4 py-3">
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.iconClass}`} aria-hidden="true" />

            <div className="min-w-0">
              {/* The severity is a WORD as well as a colour and an icon — status is never colour
                  alone (ui-ux.md § 7). Visually hidden because the heading and the icon already carry
                  it for sighted users. */}
              <span className="sr-only">{style.label}: </span>

              <p className="text-sm text-foreground">{item.summary}</p>

              {item.detail !== null && <p className="mt-0.5 text-xs text-muted">{item.detail}</p>}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The confirmation.
 *
 * It names the campaign and the NUMBER OF PEOPLE, because "Are you sure?" is not a confirmation
 * (ui-ux.md § 9). There is no undo for this, so the dialog says so rather than implying one exists.
 */
function ConfirmSendDialog({
  audienceSize,
  campaignName,
  isPending,
  onCancel,
  onConfirm,
}: {
  audienceSize: number;
  campaignName: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      title="Send this campaign?"
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>

          <Button variant="primary" onClick={onConfirm} disabled={isPending}>
            <Send className="h-4 w-4" aria-hidden="true" />
            Send to {audienceSize.toLocaleString()} people
          </Button>
        </>
      }
    >
      <p className="text-sm text-foreground">
        <strong className="text-heading">{campaignName}</strong> will be sent to{' '}
        <strong className="text-heading">{audienceSize.toLocaleString()}</strong> people.
      </p>

      <p className="mt-3 text-sm text-muted">
        This cannot be undone. You can pause the send afterwards, but anything already handed to the
        provider within ten minutes of going out cannot be recalled.
      </p>
    </Modal>
  );
}


function SendSkeleton() {
  return (
    <div className="px-4 py-6 sm:px-6">
      <Skeleton className="h-7 w-72" />
      <Skeleton className="mt-5 h-24" />
      <Skeleton className="mt-5 h-40" />
      <Skeleton className="mt-5 h-32" />
    </div>
  );
}

/**
 * Choosing the address the campaign sends from.
 *
 * Read-only once the campaign leaves Draft or InReview, matching the server: the From line is part of
 * what an approver approved, and a campaign that changed its sender after approval would be sending
 * something nobody signed off.
 *
 * Saves on CHANGE rather than behind a button. There is one field and no way to get it half-right, so
 * a Save button would be an extra click whose only purpose is to be forgotten — and the checklist
 * above re-runs on success, which is the feedback.
 */
function SenderPicker({ campaign }: { campaign: CampaignDetail }) {
  const brand = useBrand(campaign.brandId);
  const save = useSetSender(campaign.id);

  const senders = (brand.data?.senderIdentities ?? []).filter((sender) => sender.isActive);
  const isEditable = campaign.status === 'Draft' || campaign.status === 'InReview';

  if (brand.isPending) return <Skeleton className="mt-5 h-20 w-full" />;

  return (
    <div className="mt-5 rounded-lg border border-border bg-surface p-5">
      <Field
        id="sender"
        label="From"
        hint={
          senders.length === 0
            ? 'This brand has no active sender addresses. An administrator adds them in Settings.'
            : 'The address recipients see, and where replies go.'
        }
      >
        <select
          id="sender"
          value={campaign.senderIdentityId ?? ''}
          disabled={!isEditable || senders.length === 0 || save.isPending}
          onChange={(event) => save.mutate(event.target.value)}
          aria-describedby="sender-hint"
          className={fieldInputClass}
        >
          <option value="">Choose an address…</option>
          {senders.map((sender) => (
            <option key={sender.id} value={sender.id}>
              {sender.label} — {sender.fromAddress}
            </option>
          ))}
        </select>
      </Field>

      {save.isError && (
        <p className="mt-2 text-xs text-danger-ink" role="alert">
          {describeError(save.error).message}
        </p>
      )}

      {!isEditable && (
        <p className="mt-2 text-xs text-muted">
          Fixed while the campaign is {campaign.status.toLowerCase()}.
        </p>
      )}
    </div>
  );
}
